import { queryOptions, useMutation, type UseMutationResult, DefaultError } from '@tanstack/react-query';

import { specialtyRelationsService } from '@services/services/specialtyRelationsService';
import { entryTypeSpecialtyService } from '@store/EntryTypeSpeciality/entryTypeSpecialtyService';
import { FilterListHelpers } from '@utils/filterListHelpers';
import { isDefined } from '@utils/isDefined';
import { objectDifference } from '@utils/objectDifference';

import {
    SpecialtyCreateForm,
    SpecialtyListItemData,
    SpecialtyStatus,
    SpecialtyStatusUpdateForm,
    SpecialtyUpdateForm,
} from 'chaika-frontend-types/frontend-cms';
import type { Optional } from 'chaika-common-types';
import { Locale } from 'chaika-enums';

import { SpecialtyApi } from '@api/SpecialtyApi';
import { store } from '@store';
import { queryClient } from '@store/queryClient';

interface EntryTypeExternalParams {
    entryTypeId?: string;
}

interface UpdateSpecilatySatatusQuery {
    current: SpecialtyListItemData;
    params: SpecialtyStatusUpdateForm;
}

interface UpdateSpecilatyQuery {
    current: SpecialtyListItemData;
    params: SpecialtyUpdateForm & EntryTypeExternalParams;
}

interface FilterParams {
    ids?: string[];
    status?: SpecialtyStatus;
    isDiagnostic?: boolean;
    name?: string;
}

export const specialtyKeys = {
    all: ['specialties'] as const,
    lists: () => [...specialtyKeys.all, 'list'] as const,
    list: (filters: object) => [...specialtyKeys.lists(), { filters }] as const,
    details: () => [...specialtyKeys.all, 'detail'] as const,
    detail: (id: string) => [...specialtyKeys.details(), id] as const,
};

export const specialtyQueryOptions = {
    list: (locale: Optional<Locale> = store.authUser.selectedLanguage) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: specialtyKeys.list({ locale }),
            queryFn: async () => {
                const specialties = await SpecialtyApi.getList(locale);

                await specialtyRelationsService.loadServices(specialties);
                return specialties;
            },
        }),
    byId: (id: string) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: specialtyKeys.detail(id),
            queryFn: () => SpecialtyApi.get(id),
            initialData: () => {
                const allData = queryClient.getQueryData(specialtyQueryOptions.list().queryKey) ?? [];
                return allData.find(s => s.id === id);
            },
        }),
};

// Нужно для мест где берем данные вне компонента (хелпер/сервис)
export function getSpecialtyById(id: string): Optional<SpecialtyListItemData> {
    return queryClient.getQueryData(specialtyQueryOptions.byId(id).queryKey);
}

// Нужно для мест где берем данные вне компонента (хелпер/сервис)
export function getSpecialties({ name, ids, status, isDiagnostic }: FilterParams = {}): SpecialtyListItemData[] {
    const { filterFieldValueIncludesString, filterFieldValueByArray, filterFieldValue } = FilterListHelpers;
    const data = queryClient.getQueryData(specialtyQueryOptions.list().queryKey) ?? [];
    return (data ?? []).filter(
        s =>
            filterFieldValueIncludesString<SpecialtyListItemData>(s, name, 'name') &&
            filterFieldValueByArray(s, ids, 'id') &&
            filterFieldValue(s, status, 'status') &&
            filterFieldValue(s, isDiagnostic, 'isDiagnostic'),
    );
}

export function useUpdateSpeialtyStatus(): UseMutationResult<
    SpecialtyListItemData,
    DefaultError,
    UpdateSpecilatySatatusQuery
> {
    return useMutation<SpecialtyListItemData, DefaultError, UpdateSpecilatySatatusQuery>({
        mutationFn: async ({ current, params }: UpdateSpecilatySatatusQuery) => {
            await SpecialtyApi.updateStatus(current.id, params);

            return { ...current, ...params };
        },
        onSuccess: (params: SpecialtyListItemData) => {
            queryClient.setQueryData(specialtyQueryOptions.list().queryKey, oldData => {
                return oldData?.map(item => (item.id === params.id ? { ...item, ...params } : item));
            });
        },
    });
}

export function useUpdateSpeialty(): UseMutationResult<SpecialtyListItemData, DefaultError, UpdateSpecilatyQuery> {
    return useMutation<SpecialtyListItemData, DefaultError, UpdateSpecilatyQuery>({
        mutationFn: async ({ current, params: { entryTypeId, ...params } }: UpdateSpecilatyQuery) => {
            await SpecialtyApi.update(current.id, objectDifference(params, current));

            if (isDefined(entryTypeId)) {
                const { entryTypeSpecialityList } = store;

                const entryTypeSpecialty = entryTypeSpecialityList.values.find(it => it.entryTypeId === entryTypeId);

                if (entryTypeSpecialty) {
                    await entryTypeSpecialtyService.updateRelation(entryTypeSpecialty.id, {
                        entryTypeId,
                        specialtyId: current.id,
                    });
                } else {
                    await entryTypeSpecialtyService.createRelation({ entryTypeId, specialtyId: current.id });
                }
            }
            return { ...current, ...params } as SpecialtyListItemData;
        },
        onSuccess: (params: SpecialtyListItemData) => {
            queryClient.setQueryData(specialtyQueryOptions.list().queryKey, oldData => {
                return oldData?.map(item => (item.id === params.id ? { ...item, ...params } : item));
            });
        },
    });
}

export function useCreateSpeialty(): UseMutationResult<
    SpecialtyListItemData,
    DefaultError,
    SpecialtyCreateForm & EntryTypeExternalParams
> {
    return useMutation<SpecialtyListItemData, DefaultError, SpecialtyCreateForm & EntryTypeExternalParams>({
        mutationFn: async ({ entryTypeId, ...params }: SpecialtyCreateForm & EntryTypeExternalParams) => {
            await SpecialtyApi.create(params);
            if (isDefined(entryTypeId)) {
                await entryTypeSpecialtyService.createRelation({ entryTypeId, specialtyId: params.id });
            }

            return { ...params, status: SpecialtyStatus.Active } as unknown as SpecialtyListItemData;
        },
        onSuccess: (newSpecialty: SpecialtyListItemData) => {
            queryClient.setQueryData(specialtyQueryOptions.list().queryKey, oldData => {
                return oldData ? [...oldData, newSpecialty] : [newSpecialty];
            });
        },
    });
}
